import * as THREE from 'three';
import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import { graphicsOptions } from '../graphics-options.js';
import { gradeLand } from './grade.js';
import { seededRandom } from './scenery-batches.js';
import { createWindMaterial } from './wind-material.js';

const TILE = 12, GROUPS_PER_METRE = 16, BLADES = 4;
const ROOT_SHADE = .86, ROOT_SHADE_END = .3;
/** Standing crop catches the light as ripe straw: its stalks take the painted crop band's colour, this much lighter. */
const CROP_LIFT = 1.25;
/** Tiles are grown this far past the view's ground footprint, and kept until they fall this far behind it, in metres. */
const AHEAD = 12, KEEP = 36;
/** A map with no more tiles than this grows them all at once; a larger one (the Ember Coast) streams them around the view. */
const WHOLE_MAP_TILES = 120;
/** At most this many tiles stand at once; zoomed out further, the ones nearest the middle of the view win. */
const MAX_TILES = 900;
/** Milliseconds of a frame spent growing tiles, and how many released tiles' meshes are kept for reuse. */
const BUILD_BUDGET = 3, SPARE_TILES = 32;
/** Candidate roots grown between checks of the budget; each step's blade count is kept, so a density draws a whole number of steps. */
const STEP = 64;
/** Under this many pixels tall, no blade is drawn and nothing is grown: the painted ground carries the colour. */
const SMALLEST_BLADE = 2;
/** Instance room a fresh mesh starts with; it doubles as a tile grows denser. */
const FIRST_ROOM = 1024;
/** Tallest a blade reaches above its root, and how far its jitter, wind and trampling carry it past the tile: the tile's bounds. */
const REACH = 1.3, SWAY = 1;
export type GrassKind = 'ribbons' | 'cards';

function softenNormals(geometry: THREE.BufferGeometry) {
  geometry.computeVertexNormals();
  const normals = geometry.getAttribute('normal'), up = new THREE.Vector3(0, 1, 0), normal = new THREE.Vector3();
  for (let i = 0; i < normals.count; i++) {
    normal.fromBufferAttribute(normals, i).lerp(up, .7).normalize(); normals.setXYZ(i, normal.x, normal.y, normal.z);
  }
  return geometry;
}

/** Five triangles, with a narrow curved tip; roots stay at local y = 0. */
function grassRibbonGeometry() {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    -.5, 0, 0, .5, 0, 0, -.33, .42, .055, .33, .42, .055,
    -.15, .78, .18, .15, .78, .18, 0, 1, .32,
  ], 3));
  geometry.setIndex([0, 1, 2, 1, 3, 2, 2, 3, 4, 3, 5, 4, 4, 5, 6]);
  // Bend the lighting normal towards up: a field catches broad light, rather than alternating black/white strips.
  return softenNormals(geometry);
}

function grassCardGeometry() {
  const geometry = new THREE.PlaneGeometry(1, 1, 1, 2);
  geometry.translate(0, .5, 0);
  const positions = geometry.getAttribute('position');
  for (let i = 0; i < positions.count; i++) positions.setZ(i, positions.getY(i) ** 2 * .16);
  return softenNormals(geometry);
}

/** Density follows projected height, not camera distance: the game uses an orthographic camera. */
export function grassDensity(projectedHeight: number) {
  return THREE.MathUtils.clamp((projectedHeight / 8) ** 2, .045, 1);
}

/** The share of blades the player's Grass setting draws. */
function settingDensity() { return graphicsOptions.grass === 'thin' ? .5 : graphicsOptions.grass === 'off' ? 0 : 1; }

/** Where the field grows. Everything is asked once per candidate root, when its tile is built. */
export interface GrassCover {
  /** How much meadow grass grows here, 0 to 1. */
  meadow(point: Point): number;
  /** Whether a crop stalk stands here; crop ground grows no meadow grass. */
  crop?(point: Point): boolean;
  height(x: number, z: number): number;
  /** The ground's painted colour, already graded. */
  color?(x: number, z: number, target: THREE.Color): THREE.Color;
}

/** One tile, grown as far as the view has needed: its candidate roots come from its own seed, in a fixed order. */
interface Tile {
  column: number; row: number; candidates: number;
  /** Blades grown after each `STEP` of candidates; `marks[0]` is 0. */
  marks: number[];
  steps?: Generator<void, void, void>;
  mesh?: THREE.InstancedMesh; low: number; high: number; dirty: boolean;
}

/**
 * Continuous grass and crops in 12 m tiles of instanced buffers. Each tile's candidate roots come from its own seed in a fixed,
 * random order, so the first part of them is an even thinning of the whole, and a tile released and grown again comes back blade
 * for blade. A tile is grown only as far as the zoom draws it: zoomed out, a few percent of its roots; zooming in grows the rest.
 * Small maps grow every tile fully up front; larger ones grow the tiles around the view a few milliseconds a frame, every tile in
 * view thinly first and then thicker, and recycle those left far behind. Only wind uniforms and the drawn prefix change per frame.
 */
export class GrassField {
  readonly group = new THREE.Group();
  readonly wind: ReturnType<typeof createWindMaterial>;
  private readonly geometry: THREE.BufferGeometry;
  private readonly tiles = new Map<number, Tile>();
  private readonly spare: THREE.InstancedMesh[] = [];
  /** Tiles where nothing grows (the sea, a town square): asked once. */
  private readonly bare = new Set<number>();
  private readonly columns: number; private readonly rows: number;
  private readonly x0: number; private readonly z0: number;
  private readonly capacity: number;
  private readonly viewport = new THREE.Vector2(1920, 1080);
  private readonly whole: boolean;

  constructor(private readonly map: MapDefinition, private readonly kind: GrassKind, private readonly cover: GrassCover) {
    this.wind = createWindMaterial(.085, 0, 1, 1, 1, { lean: 1.3 });
    const material = this.wind.material;
    material.side = THREE.DoubleSide; material.flatShading = false;
    const baseCompile = material.onBeforeCompile, baseKey = material.customProgramCacheKey();
    material.customProgramCacheKey = () => `${baseKey}-field-${kind}-roots-${ROOT_SHADE}-${ROOT_SHADE_END}`;
    material.onBeforeCompile = (shader, renderer) => {
      baseCompile.call(material, shader, renderer);
      // Approximate neighbouring blades blocking light at the foot; the upper blade keeps its ground-matched colour.
      shader.fragmentShader = shader.fragmentShader.replace('#include <color_fragment>', `#include <color_fragment>
        diffuseColor.rgb *= mix(${ROOT_SHADE.toFixed(3)}, 1., smoothstep(0., ${ROOT_SHADE_END.toFixed(3)}, vWindTip));`);
      // Both faces represent the same leaf canopy. Flipping the softened upward normal on the back makes black needles.
      shader.fragmentShader = shader.fragmentShader.replace('#include <normal_fragment_begin>',
        '#include <normal_fragment_begin>\n normal = normalize(vNormal); nonPerturbedNormal = normal;');
      if (kind === 'cards') {
        // Four curved blade silhouettes on each opaque cutout card; no transparency sorting or alpha blending.
        shader.vertexShader = 'varying vec2 vGrassUv;\n' + shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n vGrassUv = uv;');
        shader.fragmentShader = 'varying vec2 vGrassUv;\n' + shader.fragmentShader.replace('#include <alphatest_fragment>', `
          float blade = floor(vGrassUv.x * 4.);
          float height = .72 + .28 * fract(sin(blade * 43. + 7.) * 1743.);
          float y = vGrassUv.y / height;
          float centre = .5 + sin(blade * 2.7) * y * y * .3;
          float width = .38 * pow(max(0., 1. - y), .7);
          if (y >= 1. || abs(fract(vGrassUv.x * 4.) - centre) > width) discard;
          #include <alphatest_fragment>`);
      }
    };
    this.geometry = kind === 'ribbons' ? grassRibbonGeometry() : grassCardGeometry();
    this.x0 = -map.width / 2 + 3; this.z0 = -map.depth / 2 + 3;
    this.columns = Math.max(0, Math.ceil((map.width - 6) / TILE)); this.rows = Math.max(0, Math.ceil((map.depth - 6) / TILE));
    this.capacity = TILE * TILE * GROUPS_PER_METRE * (kind === 'ribbons' ? BLADES : 1);
    this.group.name = 'grass-field';
    this.group.matrixAutoUpdate = false; this.group.matrixWorldAutoUpdate = false;
    this.whole = this.columns * this.rows <= WHOLE_MAP_TILES;
    if (this.whole) {
      for (let row = 0; row < this.rows; row++) for (let column = 0; column < this.columns; column++) this.grow(this.tile(column, row), Infinity);
      this.flush();
    }
  }

  update(time: number) { this.wind.update(time); }

  /** The tiles that have grown blades. */
  meshes() { return [...this.tiles.values()].flatMap(tile => tile.mesh && tile.marks.at(-1)! > 0 ? [tile.mesh] : []); }

  /** How tall a blade stands on screen, in pixels, for this camera. */
  private projected(camera: THREE.Camera) {
    const ortho = camera as THREE.OrthographicCamera;
    return ortho.isOrthographicCamera ? .3 * this.viewport.y * ortho.zoom / (ortho.top - ortho.bottom) : Infinity;
  }

  /** The share of a tile's candidate roots this camera draws. */
  private share(camera: THREE.Camera) {
    const projected = this.projected(camera);
    return projected < SMALLEST_BLADE ? 0 : grassDensity(projected) * settingDensity();
  }

  /**
   * Before drawing: grows the tiles in and just ahead of the view's ground footprint as far as the zoom draws them, and releases
   * those far behind it. Tiles in view are brought to a quarter of what they need first, then half, then all, nearest the middle
   * first, so a zoom-out fills in thinly at once and thickens. When the middle of the view has nothing at all (the first view, a
   * teleport) the tiles in view get their quarter at once, so grass never pops in from bare ground.
   */
  stream(camera: THREE.Camera) {
    const share = this.share(camera);
    this.group.visible = share > 0;
    if (!share || this.whole) return;
    const view = groundFootprint(camera);
    if (!view) return;
    const range = (pad: number) => ({
      c0: Math.max(0, Math.floor((view.x0 - pad - this.x0) / TILE)), c1: Math.min(this.columns - 1, Math.floor((view.x1 + pad - this.x0) / TILE)),
      r0: Math.max(0, Math.floor((view.z0 - pad - this.z0) / TILE)), r1: Math.min(this.rows - 1, Math.floor((view.z1 + pad - this.z0) / TILE)),
    });
    const keep = range(KEEP);
    for (const [key, tile] of this.tiles) {
      if (tile.column >= keep.c0 && tile.column <= keep.c1 && tile.row >= keep.r0 && tile.row <= keep.r1) continue;
      this.release(key, tile);
    }
    const want = range(AHEAD), seen = range(0), cx = (view.x0 + view.x1) / 2, cz = (view.z0 + view.z1) / 2;
    const wanted: { column: number; row: number; d: number; inView: boolean }[] = [];
    for (let row = want.r0; row <= want.r1; row++) for (let column = want.c0; column <= want.c1; column++) {
      if (this.bare.has(row * this.columns + column)) continue;
      const inView = column >= seen.c0 && column <= seen.c1 && row >= seen.r0 && row <= seen.r1;
      wanted.push({ column, row, inView, d: Math.hypot(this.x0 + (column + .5) * TILE - cx, this.z0 + (row + .5) * TILE - cz) });
    }
    wanted.sort((a, b) => a.d - b.d);
    const middle = wanted.find(item => item.inView), centre = middle && this.tiles.get(middle.row * this.columns + middle.column);
    const jump = !!middle && !(centre && centre.candidates > 0);
    const start = performance.now(), over = () => performance.now() - start > BUILD_BUDGET;
    passes: for (const [part, inViewOnly] of [[.25, true], [.5, true], [1, true], [1, false]] as const) {
      for (const { column, row, inView } of wanted) {
        if (inViewOnly && !inView) continue;
        const key = row * this.columns + column;
        if (!this.tiles.has(key) && this.tiles.size >= MAX_TILES) continue;
        const tile = this.tile(column, row), target = Math.ceil(this.total(tile) * share * part);
        if (tile.candidates >= target || !tile.steps) continue;
        const now = jump && part === .25 && inView;
        if (!now && over()) break passes;
        this.grow(tile, target, now ? undefined : over);
      }
    }
    this.flush();
  }

  dispose() {
    for (const [key, tile] of this.tiles) this.release(key, tile);
    for (const mesh of this.spare.splice(0)) { this.group.remove(mesh); mesh.dispose(); }
    this.geometry.dispose(); this.wind.material.dispose();
  }

  private tile(column: number, row: number) {
    const key = row * this.columns + column;
    let tile = this.tiles.get(key);
    if (!tile) {
      tile = { column, row, candidates: 0, marks: [0], low: Infinity, high: -Infinity, dirty: false };
      tile.steps = this.build(tile);
      this.tiles.set(key, tile);
    }
    return tile;
  }

  private total(tile: Tile) {
    const { width, depth } = this.rect(tile.column, tile.row);
    return Math.ceil(width * depth * GROUPS_PER_METRE);
  }

  private rect(column: number, row: number) {
    const x = this.x0 + column * TILE, z = this.z0 + row * TILE;
    return { x, z, width: Math.min(TILE, this.map.width / 2 - 3 - x), depth: Math.min(TILE, this.map.depth / 2 - 3 - z) };
  }

  /** Grows `tile` until it has `target` candidates, it is whole, or `stop` says the frame's budget is spent. */
  private grow(tile: Tile, target: number, stop?: () => boolean) {
    while (tile.steps && tile.candidates < target) {
      if (tile.steps.next().done) { tile.steps = undefined; break; }
      if (stop?.()) break;
    }
    if (!tile.steps && tile.marks.at(-1) === 0) {
      // Nothing grows here at all: remember it and give the tile back.
      const key = tile.row * this.columns + tile.column;
      this.release(key, tile); this.bare.add(key);
    }
  }

  /** Uploads what tiles grew this frame and fits their bounds. */
  private flush() {
    for (const tile of this.tiles.values()) {
      const mesh = tile.mesh;
      if (!tile.dirty || !mesh) continue;
      tile.dirty = false;
      const blades = tile.marks.at(-1)!;
      mesh.instanceMatrix.clearUpdateRanges(); mesh.instanceMatrix.addUpdateRange(0, blades * 16); mesh.instanceMatrix.needsUpdate = true;
      mesh.instanceColor!.clearUpdateRanges(); mesh.instanceColor!.addUpdateRange(0, blades * 3); mesh.instanceColor!.needsUpdate = true;
      const { x, z, width, depth } = this.rect(tile.column, tile.row);
      mesh.boundingBox ??= new THREE.Box3();
      mesh.boundingBox.min.set(x - SWAY, tile.low - SWAY, z - SWAY); mesh.boundingBox.max.set(x + width + SWAY, tile.high + REACH + SWAY, z + depth + SWAY);
      mesh.boundingSphere = mesh.boundingBox.getBoundingSphere(mesh.boundingSphere ?? new THREE.Sphere());
      mesh.count = blades; mesh.userData.tile = tile; mesh.visible = blades > 0;
      if (mesh.parent !== this.group) this.group.add(mesh);
    }
  }

  private release(key: number, tile: Tile) {
    this.tiles.delete(key); tile.steps?.return(); tile.steps = undefined;
    const mesh = tile.mesh;
    if (!mesh) return;
    tile.mesh = undefined; mesh.userData.tile = undefined;
    // Spares stay in the group, hidden, so the scene's release frees their buffers too.
    mesh.visible = false;
    if (this.spare.length < SPARE_TILES) this.spare.push(mesh); else { this.group.remove(mesh); mesh.dispose(); }
  }

  /** Grows one tile's candidate roots in order, `STEP` at a time, writing its blades into a mesh that grows with them. */
  private *build(tile: Tile): Generator<void, void, void> {
    const { map, kind, cover } = this, { x, z, width, depth } = this.rect(tile.column, tile.row);
    // Each tile has its own seed: grown in any order, or grown again after being released, it grows the same blades.
    const random = seededRandom((map.scenery!.seed ^ 0x674a2 ^ Math.imul(tile.column + 1, 73856093) ^ Math.imul(tile.row + 1, 19349663)) >>> 0);
    const blades = kind === 'ribbons' ? BLADES : 1, total = this.total(tile);
    let index = 0;
    for (let i = 0; i < total; i++) {
      if (i > 0 && i % STEP === 0) { tile.candidates = i; tile.marks.push(index); tile.dirty = true; yield; }
      const px = x + random() * width, pz = z + random() * depth, point = { x: px, z: pz };
      const height = .3 + random() * .16, turn = random() * Math.PI * 2, tone = random(), keep = random();
      const crop = cover.crop?.(point) ?? false;
      if (crop) {
        if (keep > .9) continue; // Gaps along the row.
      } else {
        // Warped broad growth and smaller worn pockets stay continuous across tile boundaries.
        const broad = Math.sin(px * .21 + Math.cos(pz * .13) * 2.4) * Math.cos(pz * .24 - Math.sin(px * .11) * 1.7);
        const fine = Math.sin(px * .91 + pz * .43) * Math.sin(pz * .77 - px * .34);
        const lush = .16 + .8 * THREE.MathUtils.smoothstep(broad * .75 + fine * .25, -.5, .5);
        if (keep > lush || keep > lush * cover.meadow(point)) continue; // The cheap test first: cover is at most 1.
      }
      const mesh = this.room(tile, index + blades);
      // Ground colours are already graded. Keep only a small blade-to-blade lightness change, with no separate hue palette.
      if (cover.color) cover.color(px, pz, color); else gradeLand(color.setHex(0x4f6834));
      color.multiplyScalar((.98 + tone * .04) * (crop ? CROP_LIFT : 1));
      // Crop stalks stand more than twice as tall as the sward and keep to their furrow: less scatter across it.
      const tall = crop ? 2.3 : 1, spread = crop ? .12 : .3;
      for (let b = 0; b < blades; b++) {
        const bx = px + (random() - .5) * spread, bz = pz + (random() - .5) * .3, scale = random(), y = cover.height(bx, bz) - .015;
        tile.low = Math.min(tile.low, y); tile.high = Math.max(tile.high, y);
        dummy.position.set(bx, y, bz);
        dummy.rotation.set(0, turn + b * 2.4, 0);
        dummy.scale.set(kind === 'ribbons' ? .05 + scale * .03 : .42, height * tall * (.8 + scale * .4), height);
        dummy.updateMatrix(); mesh.setMatrixAt(index, dummy.matrix); mesh.setColorAt(index++, color);
      }
    }
    tile.candidates = total; tile.marks.push(index); tile.dirty = true;
  }

  /** The tile's mesh, with room for `count` blades: a spare or a new one, doubled (and its old buffers freed) when full. */
  private room(tile: Tile, count: number) {
    let mesh = tile.mesh;
    if (!mesh) {
      mesh = tile.mesh = this.spare.pop() ?? this.tileMesh(FIRST_ROOM);
      mesh.visible = false;
    }
    const room = mesh.instanceMatrix.count;
    if (count <= room) return mesh;
    const size = Math.min(this.capacity, Math.max(count, room * 2));
    const matrices = new Float32Array(size * 16), colors = new Float32Array(size * 3);
    matrices.set(mesh.instanceMatrix.array as Float32Array); colors.set(mesh.instanceColor!.array as Float32Array);
    // `dispose` frees the old instance buffers on the GPU; the mesh carries on with the larger ones.
    mesh.dispose();
    mesh.instanceMatrix = new THREE.InstancedBufferAttribute(matrices, 16).setUsage(THREE.DynamicDrawUsage);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(colors, 3).setUsage(THREE.DynamicDrawUsage);
    mesh.count = Math.min(mesh.count, tile.marks.at(-1)!);
    return mesh;
  }

  private tileMesh(room: number) {
    const mesh = new THREE.InstancedMesh(this.geometry, this.wind.material, room);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.setColorAt(0, color); mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    mesh.name = `grass-field-${this.kind}`; mesh.receiveShadow = true; mesh.castShadow = false;
    mesh.matrixAutoUpdate = false; mesh.matrixWorldAutoUpdate = false;
    mesh.onBeforeRender = (renderer, _scene, camera) => {
      const tile = mesh.userData.tile as Tile | undefined;
      if (!tile) return;
      if (!(camera as THREE.OrthographicCamera).isOrthographicCamera) { mesh.count = tile.marks.at(-1)!; return; }
      renderer.getDrawingBufferSize(this.viewport);
      // Draw whole steps of the tile's roots: the share of them the zoom wants, or as many as have grown so far.
      const steps = Math.ceil(this.total(tile) * this.share(camera) / STEP);
      mesh.count = tile.marks[Math.min(tile.marks.length - 1, steps)];
    };
    return mesh;
  }
}

const dummy = new THREE.Object3D(), color = new THREE.Color();
const corner = new THREE.Vector3(), towards = new THREE.Vector3(), ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), ray = new THREE.Ray();

/** The bounding box, on the ground plane, of what the camera sees. */
function groundFootprint(camera: THREE.Camera) {
  camera.updateMatrixWorld();
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const [x, y] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
    corner.set(x, y, -1).unproject(camera); towards.set(x, y, 1).unproject(camera).sub(corner).normalize();
    ray.set(corner, towards);
    if (!ray.intersectPlane(ground, corner)) return undefined;
    x0 = Math.min(x0, corner.x); x1 = Math.max(x1, corner.x); z0 = Math.min(z0, corner.z); z1 = Math.max(z1, corner.z);
  }
  return { x0, x1, z0, z1 };
}
